import React, { useEffect, useState } from "react";
import { X, Sparkles, Building2, User, Target, CheckCircle2, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import CinTypeahead from "./CinTypeahead";

const SECTORS = ["Fintech", "CleanTech", "HealthTech", "SaaS", "Consumer", "MSME", "Manufacturing", "Enterprise SaaS", "AgriTech", "EdTech", "Logistics", "DeepTech", "SpaceTech", "MediaTech", "AI", "Biotech"];
const STAGES = ["Idea", "Pre-Seed", "Seed", "Pre-Series A", "Series A", "Series B", "Growth"];

const STEPS = [
  { key: "welcome", label: "Welcome" },
  { key: "entity", label: "Company" },
  { key: "sector", label: "Business" },
  { key: "founder", label: "Founder" },
  { key: "done", label: "You're set" },
];

export default function StartupOnboardingWizard({ open, onClose }) {
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    legal_entity_name: "",
    startup_name: "",
    cin: "",
    sector: "",
    business_stage: "",
    one_line_pitch: "",
    founder_name: "",
    founder_mobile: "",
  });

  useEffect(() => {
    if (!open) return;
    setIdx(0);
    // Hydrate current values so we don't overwrite prior data if founder revisits.
    api.get("/startup/me").then((r) => {
      const s = r.data?.startup || {};
      setForm({
        legal_entity_name: s.legal_entity_name || "",
        startup_name: s.startup_name || "",
        cin: s.cin || "",
        sector: s.sector || "",
        business_stage: s.business_stage || "",
        one_line_pitch: s.one_line_pitch || "",
        founder_name: s.founder_name || "",
        founder_mobile: s.founder_mobile || "",
      });
    }).catch(() => { /* silent */ });
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const persist = async (patch) => {
    setSaving(true);
    try {
      await api.put("/startup/profile", patch);
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const dismiss = async () => {
    try { await api.post("/startup/onboarding/dismiss-welcome"); } catch (e) { /* silent */ }
    onClose?.();
  };

  const next = async () => {
    // Save the current step's fields before advancing.
    const step = STEPS[idx].key;
    try {
      if (step === "entity") {
        await persist({
          legal_entity_name: form.legal_entity_name || undefined,
          startup_name: form.startup_name || form.legal_entity_name || undefined,
          cin: form.cin || undefined,
        });
      } else if (step === "sector") {
        await persist({
          sector: form.sector || undefined,
          business_stage: form.business_stage || undefined,
          one_line_pitch: form.one_line_pitch || undefined,
        });
      } else if (step === "founder") {
        await persist({
          founder_name: form.founder_name || undefined,
          founder_mobile: form.founder_mobile || undefined,
        });
      }
    } catch (e) { return; /* toast raised */ }
    if (idx === STEPS.length - 1) { await dismiss(); return; }
    setIdx((i) => Math.min(STEPS.length - 1, i + 1));
  };

  const back = () => setIdx((i) => Math.max(0, i - 1));

  if (!open) return null;
  const step = STEPS[idx].key;
  const isLast = idx === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="startup-wizard"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-2xl border border-navy/10 overflow-hidden">
        {/* Header */}
        <div className="relative px-8 py-6 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white">
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            data-testid="startup-wizard-skip"
            aria-label="Skip wizard"
          >
            <X size={20} />
          </button>
          <div className="overline text-white/70 mb-2">Founder Onboarding · Step {idx + 1} of {STEPS.length}</div>
          {/* Step dots */}
          <div className="flex items-center gap-1.5" data-testid="startup-wizard-steps">
            {STEPS.map((s, i) => (
              <div
                key={s.key}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-10 bg-white" : i < idx ? "w-6 bg-white/70" : "w-4 bg-white/25"}`}
                aria-label={s.label}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-8 min-h-[280px]">
          {step === "welcome" && (
            <div data-testid="startup-wizard-step-welcome">
              <div className="flex items-center gap-2 mb-3 text-emerald-700">
                <Sparkles size={16} />
                <div className="overline">Welcome to VenturaZ</div>
              </div>
              <div className="font-heading text-2xl text-ink mb-3">Let{"\u2019"}s get your file investor-ready.</div>
              <p className="text-ink-soft leading-relaxed mb-4">
                Four quick screens · under 4 minutes. We auto-save each step so you can pause and resume anytime.
                When you{"\u2019"}re done, hit submit and our team places your file in front of matched investors.
              </p>
              <ul className="space-y-2">
                {["Legal entity + auto-fill from MCA", "Sector, stage & pitch line", "Founder contact details"].map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-ink" data-testid={`startup-wizard-bullet-${i}`}>
                    <ArrowRight size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {step === "entity" && (
            <div data-testid="startup-wizard-step-entity">
              <div className="flex items-center gap-2 mb-3 text-emerald-700">
                <Building2 size={16} />
                <div className="overline">Company</div>
              </div>
              <div className="font-heading text-xl text-ink mb-4">Who{"\u2019"}s the legal entity?</div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Legal Entity Name</div>
                  <input
                    value={form.legal_entity_name}
                    onChange={set("legal_entity_name")}
                    className="w-full px-3 py-2.5 rounded-md border border-navy/15 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none text-ink"
                    placeholder="Acme Green Grid Pvt Ltd"
                    data-testid="startup-wizard-legal-entity"
                  />
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">CIN — pick to auto-fill</div>
                  <CinTypeahead
                    value={form.cin}
                    onChange={(v) => setForm((f) => ({ ...f, cin: v }))}
                    onCandidate={(c) => setForm((f) => ({
                      ...f,
                      cin: c.cin || f.cin,
                      legal_entity_name: c.company_legal_name || f.legal_entity_name,
                      startup_name: f.startup_name || c.company_legal_name || f.legal_entity_name,
                    }))}
                    testId="startup-wizard-cin"
                  />
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Brand / Startup Name</div>
                  <input
                    value={form.startup_name}
                    onChange={set("startup_name")}
                    className="w-full px-3 py-2.5 rounded-md border border-navy/15 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none text-ink"
                    placeholder="Green Grid"
                    data-testid="startup-wizard-brand"
                  />
                </div>
              </div>
            </div>
          )}

          {step === "sector" && (
            <div data-testid="startup-wizard-step-sector">
              <div className="flex items-center gap-2 mb-3 text-emerald-700">
                <Target size={16} />
                <div className="overline">Business</div>
              </div>
              <div className="font-heading text-xl text-ink mb-4">What do you do & where do you play?</div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Sector</div>
                  <div className="flex flex-wrap gap-2">
                    {SECTORS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, sector: s }))}
                        className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border rounded transition-colors ${
                          form.sector === s ? "border-emerald-600 text-emerald-700 bg-emerald-50" : "border-navy/15 text-ink-muted hover:border-emerald-300"
                        }`}
                        data-testid={`startup-wizard-sector-${s}`}
                      >{s}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Stage</div>
                  <div className="flex flex-wrap gap-2">
                    {STAGES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, business_stage: s }))}
                        className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border rounded transition-colors ${
                          form.business_stage === s ? "border-emerald-600 text-emerald-700 bg-emerald-50" : "border-navy/15 text-ink-muted hover:border-emerald-300"
                        }`}
                        data-testid={`startup-wizard-stage-${s}`}
                      >{s}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">One-line pitch</div>
                  <textarea
                    value={form.one_line_pitch}
                    onChange={set("one_line_pitch")}
                    rows={2}
                    maxLength={200}
                    className="w-full px-3 py-2.5 rounded-md border border-navy/15 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none text-ink resize-none"
                    placeholder="We help MSMEs cut power bills by 40% with rooftop solar-as-a-service."
                    data-testid="startup-wizard-pitch"
                  />
                  <div className="text-[10px] font-mono text-ink-faint text-right mt-1">{(form.one_line_pitch || "").length}/200</div>
                </div>
              </div>
            </div>
          )}

          {step === "founder" && (
            <div data-testid="startup-wizard-step-founder">
              <div className="flex items-center gap-2 mb-3 text-emerald-700">
                <User size={16} />
                <div className="overline">Founder</div>
              </div>
              <div className="font-heading text-xl text-ink mb-4">Who runs the show?</div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Founder Name</div>
                  <input
                    value={form.founder_name}
                    onChange={set("founder_name")}
                    className="w-full px-3 py-2.5 rounded-md border border-navy/15 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none text-ink"
                    placeholder="Priya Sharma"
                    data-testid="startup-wizard-founder-name"
                  />
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Mobile (WhatsApp preferred)</div>
                  <input
                    value={form.founder_mobile}
                    onChange={set("founder_mobile")}
                    className="w-full px-3 py-2.5 rounded-md border border-navy/15 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none text-ink"
                    placeholder="+91 98XXXXXXX"
                    data-testid="startup-wizard-founder-mobile"
                  />
                </div>
              </div>
            </div>
          )}

          {step === "done" && (
            <div className="text-center py-4" data-testid="startup-wizard-step-done">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <div className="font-heading text-2xl text-ink mb-2">You{"\u2019"}re all set!</div>
              <p className="text-ink-soft max-w-md mx-auto">
                Your basics are saved. Head to the dashboard to complete KYC, upload your pitch deck,
                and hit submit whenever you{"\u2019"}re ready.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-navy/10 bg-navy/[0.02] flex items-center justify-between">
          <button
            onClick={dismiss}
            className="text-sm text-ink-muted hover:text-ink transition-colors"
            data-testid="startup-wizard-skip-footer"
          >
            Skip for now
          </button>
          <div className="flex items-center gap-2">
            {idx > 0 && !isLast && (
              <button
                onClick={back}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-navy/15 text-ink text-sm hover:bg-navy/[0.04] transition-colors"
                data-testid="startup-wizard-back"
              >
                <ArrowLeft size={14} /> Back
              </button>
            )}
            <button
              onClick={next}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-heading transition-colors"
              data-testid="startup-wizard-next"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {isLast ? "Go to dashboard" : idx === 0 ? "Let's go" : "Continue"}
              {!isLast && <ArrowRight size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
